import { zodResolver } from '@hookform/resolvers/zod';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckIcon from '@mui/icons-material/CheckOutlined';
import GoogleIcon from '@mui/icons-material/Google';
import {
  Box,
  GlassAlert,
  GlassButton,
  GlassChip,
  GlassTextField,
  iconSizes,
  Link,
  ShimmerLoader,
  StatusPage,
  Typography,
  useI18n,
  useTerminology,
} from '@ops-matrix/design-system-ui';
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  getRedirectResult,
  signInWithRedirect,
  updateProfile,
} from 'firebase/auth';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { auth } from '../config/firebase';
import { useRedirect } from '../hooks/useRedirect';
import { useTheme } from '../theme/ThemeProvider';
import { isReturningFromFirebaseRedirect } from '../utils/firebase-redirect';

interface RegisterPageProps {
  isConsumer?: boolean;
}

/**
 * Annual originations chip options — single-select.
 * The keys map to localized labels in en.ts; the values are the canonical
 * identifiers stored in form state and (eventually) the backend payload.
 */
const ORIGINATION_OPTIONS = [
  { value: 'under_50m', i18nKey: 'auth.signUp.annualOriginations.under50M' as const },
  { value: '50m_to_250m', i18nKey: 'auth.signUp.annualOriginations.50to250M' as const },
  { value: '250m_to_1b', i18nKey: 'auth.signUp.annualOriginations.250Mto1B' as const },
  { value: 'over_1b', i18nKey: 'auth.signUp.annualOriginations.over1B' as const },
] as const;

/**
 * Products chip options — multi-select. Same key/value split as originations.
 */
const PRODUCT_OPTIONS = [
  { value: 'bridge', i18nKey: 'auth.signUp.products.bridge' as const },
  { value: 'dscr', i18nKey: 'auth.signUp.products.dscr' as const },
  { value: 'construction', i18nKey: 'auth.signUp.products.construction' as const },
  { value: 'commercial', i18nKey: 'auth.signUp.products.commercial' as const },
] as const;

type OriginationValue = (typeof ORIGINATION_OPTIONS)[number]['value'];
type ProductValue = (typeof PRODUCT_OPTIONS)[number]['value'];

export default function RegisterPage({ isConsumer = false }: RegisterPageProps) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { term } = useTerminology();
  const platformName = term('navigation.platformName');
  const { portalType, tenantId, leadId, businessId } = useTheme();
  // useRedirect handles the full allowlist gate (platform domains +
  // pre-verified custom domains) and falls back to the portal default.
  // Replaces a bespoke helper that forgot to thread `verifiedCustomDomains`,
  // dropping white-label tenants on custom domains. Refs: gap-finder F-G.
  const { redirect } = useRedirect();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [leadData, setLeadData] = useState<{ name?: string; email?: string } | null>(null);
  const [pendingReview, setPendingReview] = useState<{
    message: string;
    matchedBusiness?: string;
  } | null>(null);
  // True on first render when we've just landed back from Google
  // signInWithRedirect — show a "Completing sign-in" view instead of the
  // form. Cleared in the getRedirectResult effect once the result resolves
  // (or fails), at which point we either navigate away (success) or fall
  // back to the form for retry (no result / error).
  const [completingRedirect, setCompletingRedirect] = useState<boolean>(isReturningFromFirebaseRedirect);

  // Partner invitation detection (Issue #192)
  const inviteToken = searchParams.get('invite');
  const inviteType = searchParams.get('type');
  const isPartnerInvite = !!(inviteToken && inviteType === 'partner');

  // Determine which form variant to render. The v3 "request access" form
  // (firstName + lastName + company + originations + products) is the
  // BUSINESS lender flow. Consumer + partner-invite flows are simpler
  // (name + email + password) since the platform already knows the tenant
  // context from the invite link.
  const isConsumerFlow = isConsumer || portalType === 'consumer';
  const showRequestAccessForm = !isConsumerFlow && !isPartnerInvite;

  // Get referral code from URL params (e.g., ?ref=ABC123) - Issue #192
  const referralCodeFromUrl = searchParams.get('ref');

  // Decode partner invite token
  const [partnerEmail, setPartnerEmail] = useState<string | null>(null);
  useEffect(() => {
    if (isPartnerInvite && inviteToken) {
      try {
        const decoded = JSON.parse(atob(inviteToken.replace(/_/g, '/').replace(/-/g, '+')));
        setPartnerEmail(decoded.email);
      } catch (_err) {
        setError(t('auth.register.invalidInvitationLink'));
      }
    }
  }, [isPartnerInvite, inviteToken, t]);

  // Fetch lead data for form pre-fill when leadId is present
  useEffect(() => {
    if (leadId && businessId) {
      const fetchLeadData = async () => {
        try {
          const response = await fetch(`${import.meta.env.VITE_API_URL}/auth/lead/${leadId}`, {
            headers: {
              'x-business-id': businessId,
            },
          });
          if (response.ok) {
            const data = await response.json();
            if (data.name || data.email) {
              setLeadData(data);
            }
          }
        } catch (err) {
          // Silently fail - form pre-fill is a convenience, not critical
          console.warn('[RegisterPage] Lead lookup failed:', err);
        }
      };
      fetchLeadData();
    }
  }, [leadId, businessId]);

  // Single unified schema. All conditional fields are validated in a
  // superRefine pass so the inferred TS type is stable across the
  // request-access (business lender) and simple (consumer / partner)
  // flows. Branching at validation time only — RHF stays happy with one
  // resolver/type pair.
  const registerSchema = z
    .object({
      firstName: z.string().min(1, t('validation.required')),
      lastName: z.string().min(1, t('validation.required')),
      email: z.string().email(t('validation.email')),
      // company / annualOriginations / products are conditionally required:
      // their presence is enforced in superRefine() below (and skipped on
      // the consumer + partner-invite flows). Keep them optional in the
      // base schema so a single `useForm` type covers both flows.
      company: z.string().optional(),
      annualOriginations: z
        .enum(ORIGINATION_OPTIONS.map((o) => o.value) as [OriginationValue, ...OriginationValue[]])
        .optional(),
      products: z.array(z.enum(PRODUCT_OPTIONS.map((p) => p.value) as [ProductValue, ...ProductValue[]])).optional(),
      password: z.string().min(8, t('validation.minLength', { min: 8 })),
      confirmPassword: z.string(),
      referralCode: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      if (data.password !== data.confirmPassword) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('validation.passwordMismatch'),
          path: ['confirmPassword'],
        });
      }
      // Business lender request-access flow requires the extra context fields.
      if (showRequestAccessForm) {
        if (!data.company || data.company.trim().length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('validation.required'),
            path: ['company'],
          });
        }
        if (!data.annualOriginations) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('validation.required'),
            path: ['annualOriginations'],
          });
        }
        if (!data.products || data.products.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('validation.required'),
            path: ['products'],
          });
        }
      }
    });

  type RegisterFormData = z.infer<typeof registerSchema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      company: '',
      products: [],
      password: '',
      confirmPassword: '',
      referralCode: referralCodeFromUrl || undefined,
    },
  });

  // Watch chip-group fields so we can render their selected state.
  const selectedOriginations = watch('annualOriginations');
  const selectedProducts = watch('products') ?? [];

  const toggleOrigination = useCallback(
    (value: OriginationValue) => {
      // Single-select — clicking the active chip clears, otherwise replaces.
      setValue('annualOriginations', selectedOriginations === value ? undefined : value, {
        shouldValidate: true,
      });
    },
    [selectedOriginations, setValue],
  );

  const toggleProduct = useCallback(
    (value: ProductValue) => {
      // Multi-select — flip membership.
      const next = selectedProducts.includes(value)
        ? selectedProducts.filter((p) => p !== value)
        : [...selectedProducts, value];
      setValue('products', next, { shouldValidate: true });
    },
    [selectedProducts, setValue],
  );

  // Pre-fill referral code if provided in URL
  useEffect(() => {
    if (referralCodeFromUrl) {
      setValue('referralCode', referralCodeFromUrl);
    }
  }, [referralCodeFromUrl, setValue]);

  // Pre-fill partner email if invite
  useEffect(() => {
    if (partnerEmail) {
      setValue('email', partnerEmail);
    }
  }, [partnerEmail, setValue]);

  // Pre-fill form with lead data (name and email from lead lookup).
  // Split a single "First Last" string into firstName/lastName best-effort —
  // the backend lead record predates the split-name form.
  useEffect(() => {
    if (leadData) {
      if (leadData.name) {
        const trimmed = leadData.name.trim();
        const firstSpace = trimmed.indexOf(' ');
        if (firstSpace > 0) {
          setValue('firstName', trimmed.slice(0, firstSpace));
          setValue('lastName', trimmed.slice(firstSpace + 1));
        } else {
          setValue('firstName', trimmed);
        }
      }
      if (leadData.email) {
        setValue('email', leadData.email);
      }
    }
  }, [leadData, setValue]);

  const handleRedirect = useCallback(
    async (idToken: string, referralCode?: string) => {
      try {
        // For consumers, register with the API first (Issue #192)
        const endpoint = isConsumerFlow
          ? `${import.meta.env.VITE_API_URL}/auth/consumer/register`
          : `${import.meta.env.VITE_API_URL}/auth/exchange`;

        const body = isConsumerFlow
          ? { firebaseIdToken: idToken, tenantId, referralCode } // Include referralCode for consumers
          : { firebaseIdToken: idToken };

        // Validate tenantId before making request (better error message)
        if (isConsumerFlow && !tenantId) {
          console.error(
            '[RegisterPage] Missing tenantId for consumer registration. URL params:',
            window.location.search,
          );
          throw new Error('Missing tenant ID. Please use the registration link provided by the business.');
        }

        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });

        if (!response.ok) {
          // Try to get more details from the error response
          const errorData = await response.json().catch(() => ({}));
          console.error('[RegisterPage] Registration failed:', { status: response.status, errorData });

          // Issue #703: Detect duplicate business (409 with structured code)
          if (response.status === 409 && errorData.code === 'DUPLICATE_BUSINESS_DETECTED') {
            setPendingReview({
              message: errorData.message,
              matchedBusiness: errorData.matchedBusiness,
            });
            return;
          }

          throw new Error(errorData.message || t('auth.register.registrationFailed'));
        }

        const data = await response.json();
        const accessToken = data.accessToken || data.token;

        redirect(accessToken);
      } catch (err) {
        setError(t('auth.register.registrationFailed'));
        console.error('Registration failed:', err);
      }
    },
    [isConsumerFlow, tenantId, redirect, t],
  );

  const onSubmit = async (data: RegisterFormData) => {
    setIsLoading(true);
    setError(null);

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, data.email, data.password);
      const displayName = `${data.firstName} ${data.lastName}`.trim();
      await updateProfile(userCredential.user, { displayName });
      const idToken = await userCredential.user.getIdToken();
      await handleRedirect(idToken, data.referralCode);
    } catch (err) {
      const firebaseError = err as { code?: string };
      if (firebaseError.code === 'auth/email-already-in-use') {
        setError(t('auth.register.emailAlreadyExists'));
      } else if (firebaseError.code === 'auth/weak-password') {
        setError(t('auth.register.weakPassword'));
      } else {
        setError(t('auth.register.registrationError'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const provider = new GoogleAuthProvider();
      // signInWithRedirect navigates the top-level window to Google's OAuth
      // endpoint and back. The result is consumed by the useEffect below on
      // the next page mount via getRedirectResult(). Switched from
      // signInWithPopup because the popup variant relies on a hidden iframe
      // at <project>.firebaseapp.com plus a third-party cookie — both
      // blocked by default on Chrome 127+ / Firefox, which silently breaks
      // popup-based Google sign-up. This mirrors the LoginPage fix from
      // PR #1555.
      await signInWithRedirect(auth, provider);
      // Execution does not continue past this line — the browser navigates
      // away. No need to reset isLoading; the redirect handles it.
    } catch (err) {
      // Pre-redirect failures only (config, persistence, network).
      // Post-redirect errors land in the getRedirectResult effect below.
      setError(t('auth.register.googleSignUpFailed'));
      console.error('Google sign-up (pre-redirect) error:', err);
      setIsLoading(false);
    }
  };

  /**
   * Process the redirect-based sign-up result on every page mount.
   * Mirrors the LoginPage handler. Referral / invite / lead context comes
   * from URL params which survive the OAuth round-trip.
   */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await getRedirectResult(auth);
        if (cancelled) return;
        if (!result) {
          // No pending redirect (fresh page load) — drop the loader and
          // render the form.
          setCompletingRedirect(false);
          return;
        }
        const idToken = await result.user.getIdToken();
        // Re-read referral code from URL — search params survive the
        // OAuth redirect intact.
        await handleRedirect(idToken, referralCodeFromUrl || undefined);
        if (!cancelled) setCompletingRedirect(false);
      } catch (err) {
        if (cancelled) return;
        setError(t('auth.register.googleSignUpFailed'));
        setCompletingRedirect(false);
        console.error('Google redirect-result error:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Run-once on mount. See LoginPage for the rationale on the empty deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentParams = searchParams.toString();
  const queryString = currentParams ? `?${currentParams}` : '';

  // Dynamic titles based on context.
  // - Partner invite uses partner copy
  // - Consumer flow uses consumer copy
  // - Business lender flow uses the v3 "request access" copy (with platformName interpolation)
  const title = isPartnerInvite
    ? t('auth.signUp.partnerTitle')
    : isConsumerFlow
      ? t('auth.signUp.consumerTitle')
      : t('auth.signUp.title');

  const subtitle = isPartnerInvite
    ? t('auth.signUp.partnerSubtitle')
    : isConsumerFlow
      ? t('auth.signUp.consumerSubtitle')
      : t('auth.signUp.subtitle', { platformName });

  // Issue #703: Show pending review page when duplicate business detected
  if (pendingReview) {
    return (
      <StatusPage
        severity="info"
        icon={<AccessTimeIcon sx={{ fontSize: iconSizes['4xl'] }} />}
        title={t('domainIntelligence.signupPendingReview.title')}
        description={pendingReview.message}
        detail={
          pendingReview.matchedBusiness ? (
            <GlassAlert severity="info" visible sx={{ mx: 'auto' }}>
              {t('domainIntelligence.signupPendingReview.matchInfo', {
                business: pendingReview.matchedBusiness,
              })}
            </GlassAlert>
          ) : undefined
        }
        action={
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
            <Typography variant="body2" color="textSecondary">
              {t('domainIntelligence.signupPendingReview.nextSteps')}
            </Typography>
            <GlassButton variant="outlined" onClick={() => navigate('/login')}>
              {t('common.backToLogin')}
            </GlassButton>
          </Box>
        }
      />
    );
  }

  if (completingRedirect) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2.5,
          minHeight: 240,
          py: 4,
        }}
      >
        <ShimmerLoader variant="circular" width={40} height={40} />
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {t('auth.completingSignIn')}
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', maxWidth: 320 }}>
          {t('auth.completingSignInSubtext')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {/* Page header (Request access + subtitle) */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" sx={{ fontWeight: 600, letterSpacing: '-0.01em', mb: 0.75 }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
          {subtitle}
        </Typography>
      </Box>

      <GlassAlert severity="error" visible={!!error} sx={{ mb: 2 }}>
        {error}
      </GlassAlert>

      <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ display: 'flex', flexDirection: 'column' }}>
        {/* First / Last name row */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
          <GlassTextField
            {...register('firstName')}
            label={t('auth.signUp.firstNameLabel')}
            autoComplete="given-name"
            disabled={isLoading}
            error={!!errors.firstName}
            helperText={errors.firstName?.message}
            fullWidth
          />
          <GlassTextField
            {...register('lastName')}
            label={t('auth.signUp.lastNameLabel')}
            autoComplete="family-name"
            disabled={isLoading}
            error={!!errors.lastName}
            helperText={errors.lastName?.message}
            fullWidth
          />
        </Box>

        {/* Work email — locked on partner invites */}
        <GlassTextField
          {...register('email')}
          label={t('auth.signUp.workEmailLabel')}
          type="email"
          autoComplete="email"
          disabled={isLoading || isPartnerInvite}
          error={!!errors.email}
          helperText={errors.email?.message || t('auth.signUp.workEmailHelper')}
          fullWidth
          sx={{ mb: 2 }}
        />

        {/* Business request-access form only: company + chip groups */}
        {showRequestAccessForm && (
          <>
            <GlassTextField
              {...register('company')}
              label={t('auth.signUp.companyLabel')}
              autoComplete="organization"
              disabled={isLoading}
              error={!!errors.company}
              helperText={errors.company?.message}
              fullWidth
              sx={{ mb: 2.5 }}
            />

            {/* Annual originations — single-select chip group */}
            <Box sx={{ mb: 2.5 }}>
              <Typography
                variant="body2"
                component="div"
                role="group"
                aria-label={t('auth.signUp.annualOriginationsAriaLabel')}
                sx={{ fontWeight: 500, color: 'text.secondary', mb: 1 }}
              >
                {t('auth.signUp.annualOriginationsLabel')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                {ORIGINATION_OPTIONS.map((opt) => {
                  const isSelected = selectedOriginations === opt.value;
                  return (
                    <GlassChip
                      key={opt.value}
                      label={t(opt.i18nKey)}
                      size="small"
                      clickable
                      color="success"
                      variant={isSelected ? 'filled' : 'outlined'}
                      onClick={() => toggleOrigination(opt.value)}
                      disabled={isLoading}
                      sx={{ height: 28 }}
                    />
                  );
                })}
              </Box>
              {errors.annualOriginations && (
                <Typography variant="caption" sx={{ color: 'error.main', mt: 0.5, display: 'block' }}>
                  {errors.annualOriginations.message as string}
                </Typography>
              )}
            </Box>

            {/* Products — multi-select chip group with check icon when on */}
            <Box sx={{ mb: 2.5 }}>
              <Typography
                variant="body2"
                component="div"
                role="group"
                aria-label={t('auth.signUp.productsAriaLabel')}
                sx={{ fontWeight: 500, color: 'text.secondary', mb: 1 }}
              >
                {t('auth.signUp.productsLabel')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                {PRODUCT_OPTIONS.map((opt) => {
                  const isSelected = selectedProducts.includes(opt.value);
                  return (
                    <GlassChip
                      key={opt.value}
                      label={t(opt.i18nKey)}
                      size="small"
                      clickable
                      color="success"
                      variant={isSelected ? 'filled' : 'outlined'}
                      icon={isSelected ? <CheckIcon /> : undefined}
                      onClick={() => toggleProduct(opt.value)}
                      disabled={isLoading}
                      sx={{ height: 28 }}
                    />
                  );
                })}
              </Box>
              {errors.products && (
                <Typography variant="caption" sx={{ color: 'error.main', mt: 0.5, display: 'block' }}>
                  {errors.products.message as string}
                </Typography>
              )}
            </Box>
          </>
        )}

        {/* Referral code — only on consumer-flow (per the original logic) */}
        {isConsumerFlow && !isPartnerInvite && (
          <GlassTextField
            {...register('referralCode')}
            label={t('auth.register.referralCode')}
            disabled={isLoading}
            error={!!errors.referralCode}
            helperText={errors.referralCode?.message || t('auth.register.referralCodeHelperText')}
            fullWidth
            sx={{ mb: 2 }}
          />
        )}

        {/* Password + confirm */}
        <GlassTextField
          {...register('password')}
          label={t('auth.signUp.passwordLabel')}
          type="password"
          autoComplete="new-password"
          disabled={isLoading}
          error={!!errors.password}
          helperText={errors.password?.message || t('auth.signUp.passwordHelper')}
          fullWidth
          sx={{ mb: 2 }}
        />
        <GlassTextField
          {...register('confirmPassword')}
          label={t('auth.signUp.confirmPasswordLabel')}
          type="password"
          autoComplete="new-password"
          disabled={isLoading}
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword?.message}
          fullWidth
          sx={{ mb: 2.5 }}
        />

        {/* Primary submit */}
        <GlassButton type="submit" variant="contained" color="success" fullWidth size="large" disabled={isLoading}>
          {isLoading ? <ShimmerLoader variant="circular" width={24} height={24} /> : t('auth.signUp.submitLabel')}
        </GlassButton>

        {/* Terms + Privacy footnote */}
        <Box sx={{ mt: 1.75, textAlign: 'center' }}>
          <Typography variant="caption" sx={{ color: 'text.disabled', lineHeight: 1.55 }}>
            {t('auth.signUp.termsPrefix')}{' '}
            <Link
              component={RouterLink}
              to="/terms"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: 'success.main' }}
            >
              {t('auth.signUp.termsLabel')}
            </Link>{' '}
            {t('auth.signUp.termsConjunction')}{' '}
            <Link
              component={RouterLink}
              to="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: 'success.main' }}
            >
              {t('auth.signUp.privacyLabel')}
            </Link>
            .
          </Typography>
        </Box>
      </Box>

      {/* Mono divider "OR CONTINUE WITH" */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, my: 2.5 }}>
        <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
        <Typography
          variant="overline"
          sx={{
            color: 'text.disabled',
            fontFamily: 'ui-monospace, "SF Mono", Menlo, Monaco, Consolas, monospace',
            letterSpacing: '0.1em',
          }}
        >
          {t('auth.signUp.orContinueWith')}
        </Typography>
        <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
      </Box>

      {/* Continue with Google */}
      <GlassButton
        variant="subtle"
        fullWidth
        size="large"
        startIcon={<GoogleIcon />}
        onClick={handleGoogleSignUp}
        disabled={isLoading}
      >
        {t('auth.signUp.continueWithGoogle')}
      </GlassButton>

      {/* Footer: already-have-account link */}
      <Box sx={{ mt: 3.5, pt: 2.5, borderTop: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('auth.signUp.footerHaveAccount')}{' '}
          <Link component={RouterLink} to={`/login${queryString}`} sx={{ color: 'success.main', fontWeight: 600 }}>
            {t('auth.signUp.footerSignIn')} →
          </Link>
        </Typography>
      </Box>
    </Box>
  );
}
